import { useEffect, useState } from "react";

import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { Check, Clock, Lock, Mail, Shield } from "lucide-react";

import { Button } from "../../common/customDesignedComponents/button";

const VerificationMethod = ({
  handleSelect,
  handleBack,
}: {
  handleSelect: () => void;
  handleBack: () => void;
}) => {
  const [selectedMethod, setSelectedMethod] = useState<"mail" | null>("mail");

  useEffect(() => {
    // Scroll to top of the page
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);
  return (
    <>
      <Helmet>
        <title>Verification Method | EquityProtect - Owner Verification</title>
        <meta
          name="description"
          content="Verify your property ownership through mail verification."
        />
      </Helmet>

      <div className="min-h-screen pb-16">
        <div className="container mx-auto px-4">
          {/* Main Content */}
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden"
            >
              {/* Secure Connection Bar */}
              <div className="bg-gradient-to-r from-secondary to-cyan-400 py-3 px-4 flex items-center justify-center gap-2">
                <Lock className="h-4 w-4 text-white" />
                <span className="text-white font-medium text-sm">
                  Secure connection
                </span>
              </div>

              {/* Form Content */}
              <div className="p-8 md:p-10">
                <h1 className="text-xl md:text-2xl font-display font-medium text-foreground text-center mb-4">
                  We now need to verify you are the owner
                </h1>

                <p className="text-muted-foreground text-center mb-8 leading-relaxed max-w-3xl mx-auto">
                  After you complete your registration, EquityProtect will mail
                  you a postcard that you should receive within approximately
                  three to four days. This postcard contains a unique validation
                  code that helps us confirm your identity and protect your
                  property. Once you receive it, you have two options: you may
                  return to the EquityProtect website and enter your code to
                  complete the verification process, or you may contact our
                  Customer Service team, and one of our specialists will assist
                  you in validating your account using your code.
                </p>

                {/* Mail Verification Option */}
                <div className="max-w-md mx-auto mb-8">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedMethod("mail")}
                    className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all duration-300 ${
                      selectedMethod === "mail"
                        ? "border-secondary bg-secondary/5 shadow-lg"
                        : "border-border hover:border-secondary/50 bg-card"
                    }`}
                  >
                    {selectedMethod === "mail" && (
                      <div className="absolute top-4 right-4">
                        <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col items-center text-center">
                      <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mb-4">
                        <Mail className="w-8 h-8 text-secondary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        Mail Verification (USPS)
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-amber-600 font-medium mb-3">
                        <Clock className="w-4 h-4" />
                        <span>3-5 Business Days</span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        We will mail a postcard with a unique 6-digit
                        verification code to your property address. Simply enter
                        the code when you log back in to complete verification.
                      </p>
                      <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                        <Shield className="w-4 h-4" />
                        <span>Secure USPS Delivery</span>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Select Button */}
                <div className="flex flex-col items-center gap-4">
                  <Button
                    onClick={handleSelect}
                    disabled={!selectedMethod}
                    className="bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 hover:to-cyan-400/90 text-white font-medium px-16 py-3 text-base rounded-full shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    VALIDATE
                  </Button>
                </div>

                {/* Back Link */}
                <div className="mt-6">
                  <button
                    onClick={handleBack}
                    className="text-secondary hover:text-secondary/80 font-medium text-sm transition-colors"
                  >
                    BACK
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VerificationMethod;
